import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  savingsVaultCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { savingsEndpoints } from "@/features/savings/api/endpoints";
import type {
  SavingsMovementDto,
  SavingsMovementRequestDto,
} from "@/features/savings/api/dto";

/**
 * Retire `amountMinor` du coffre vers un portefeuille principal du titulaire
 * (`POST /SavingsVaults/{id}/withdraw`) — refusé en 422 tant que le coffre
 * est bloqué (le message indique l'échéance). Produit `SAVINGS_WITHDRAWAL`,
 * gratuit par défaut. Mêmes statuts de mouvement que `deposit`.
 *
 * Idempotency-Key OBLIGATOIRE (guide §4.7, clé persistée par empreinte du
 * corps).
 */
export const useWithdrawSavingsVault = (vaultId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["savings", "savingsVaults", "withdraw", vaultId],
    mutationFn: (body: SavingsMovementRequestDto) =>
      mutateIdempotent<SavingsMovementDto>({
        scopeId: `savings.savingsVaults.withdraw.${vaultId}`,
        url: savingsEndpoints.savingsVaults.withdraw(vaultId),
        body,
      }),
    onSuccess: () =>
      invalidateAfterMutation(queryClient, savingsVaultCacheKeys(vaultId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useWithdrawSavingsVault failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "savings" }
      );
    },
  });
};
